import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Plus, Search, Edit, Trash2, Eye, RefreshCw, ChevronDown, CalendarIcon } from "lucide-react";
import { ImportExportActions, ImportMode, generateLanguageCertificateTemplate } from "./ImportExportUtils";
import LanguageCertificateModal from "./modals/LanguageCertificateModal";
import { notify } from "@/lib/utils/toast";
import { languageCertificatesApi, type LanguageCertificate, type LanguageCertificateFilters } from "../lib/api";
import { useAuthStore, PERMISSIONS } from "../lib/stores/auth";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { LanguageCertificateDetail } from "./modals/LanguageCertificateDetail";
import { format, parse } from "date-fns";
import { StatusBadge } from "./StatusBadge";
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { cn } from "./ui/utils";
import { PaginationIndex } from "./PaginationIndex";
import { iLanguageCertificate } from "@/types/models";
import { languageLevelOptions, requestTypeList } from "./validations/shared";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";
import { vi } from "date-fns/locale";
import { AccessDenied } from "./AccessDenied";
import { NgocInput } from "./ui/ngocInput";
import { validateLanguageCertificateImportData, validateLanguageCertificateImportDataInDB } from "./validations/languageCertificate";
import { SmoothLoadingWrapper } from "./animate/SmoothLoadingWrapper";

export default function LanguageCertificateManagement() {
	const { hasPermission } = useAuthStore();
	const [certificateInformation, setCertificateInformation] = useState<LanguageCertificate | null>(null);
	const [certificates, setCertificates] = useState<LanguageCertificate[]>([]);
	const [loading, setLoading] = useState(true);
	const [searchTerm, setSearchTerm] = useState("");
	const [requestTypeFilter, setRequestTypeFilter] = useState("");
	const [languageFilter, setLanguageFilter] = useState("");
	const [languageLevelFilter, setLanguageLevelFilter] = useState("");
	const [dateFromFilter, setDateFromFilter] = useState("");
	const [dateToFilter, setDateToFilter] = useState("");
	const [isModalOpen, setModalOpen] = useState(false);
	const [editingCertificate, setEditingCertificate] = useState<LanguageCertificate | null>(null);
	const [currentPage, setCurrentPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [totalCount, setTotalCount] = useState(0);
	const [itemsPerPage, setItemsPerPage] = useState(10);
	const [deleteItemId, setDeleteItemId] = useState<string | null>(null);
	const [modalMode, setModalMode] = useState<"create" | "edit">("create");
	const [isFiltering, setIsFiltering] = useState(false);
	const [autoFilters, setAutoFilters] = useState<LanguageCertificateFilters>({});
	const [openCourseStartSelected, setOpenCourseStartSelected] = useState(false);
	const [openCourseEndSelected, setOpenCourseEndSelected] = useState(false);


	// Permission checks
	const canView = hasPermission(PERMISSIONS.languageCertificates.permissions.view.key);
	const canCreate = hasPermission(PERMISSIONS.languageCertificates.permissions.create.key);
	const canUpdate = hasPermission(PERMISSIONS.languageCertificates.permissions.update.key);
	const canDelete = hasPermission(PERMISSIONS.languageCertificates.permissions.delete.key);
	const canImport = hasPermission(PERMISSIONS.languageCertificates.permissions.import.key);
	const canExport = hasPermission(PERMISSIONS.languageCertificates.permissions.create.key);

	if (!canView) return <AccessDenied name="danh sách chứng chỉ ngoại ngữ" />;

	const getFullRecord = async (filter: LanguageCertificateFilters = {}) => {
		const result = await languageCertificatesApi.getAll({
			...filter,
			page: 1,
			limit: 999999
		});
		return result.data.data || [];
	};

	const fetchList = useCallback(async (customFilters?: LanguageCertificateFilters) => {
		let finalFilter = customFilters || autoFilters;
		try {
			setLoading(true);

			const response = await languageCertificatesApi.getAll(finalFilter);

			if (response && response.success && response.data) {
				setCertificates(response.data.data || []);
				setTotalPages(response.data.totalPages || 1);
				setTotalCount(response.data.total || 0);
			} else {
				// Fallback to empty state
				setCertificates([]);
				setTotalPages(1);
				setTotalCount(0);
			}
		} catch (error) {
			console.error("Error loading language certificates:", error);

			setCertificates([]);
			setTotalPages(1);
			setTotalCount(0);
		} finally {
			setLoading(false);
		}
	}, [autoFilters]);

	useEffect(() => {
		fetchList({
			...autoFilters,
			page: currentPage,
		});
	}, [currentPage]);

	useEffect(() => {
		if (currentPage !== 1) {
			setCurrentPage(1);
		}
	}, [itemsPerPage]);

	const [languages, setLanguages] = useState<string[]>([]);

	const languageOptions = [
		{ value: "Tiếng Anh", label: "Tiếng Anh" },
		{ value: "Tiếng Nhật", label: "Tiếng Nhật" },
		{ value: "Tiếng Trung", label: "Tiếng Trung" },
		{ value: "Tiếng Hàn", label: "Tiếng Hàn" },
		{ value: "Tiếng Pháp", label: "Tiếng Pháp" },
		{ value: "Tiếng Đức", label: "Tiếng Đức" },
	];

	const toggleSelectLanguage = (val: string) => {
		const displayData = languages.includes(val) ? languages.filter((v) => v !== val) : [...languages, val];
		setLanguages(displayData);
		handleFilterChange(setLanguageFilter, displayData.join(","));
	};

	const [languageLevels, setLanguageLevels] = useState<string[]>([]);

	const toggleSelectLanguageLevel = (val: string) => {
		const displayData = languageLevels.includes(val) ? languageLevels.filter((v) => v !== val) : [...languageLevels, val];
		setLanguageLevels(displayData);
		handleFilterChange(setLanguageLevelFilter, displayData.join(","));
	};

	const handleImport = async (data: any[], mode: ImportMode) => {
		if (!canImport) {
			notify.error("Bạn không có quyền import chứng chỉ");
			throw new Error("Không có quyền import");
		}

		try {
			const importedDataFromExcel: Partial<LanguageCertificate>[] = data.map((row) => ({
				cccd: row["CCCD (*)"],
				studentId: row["Số báo danh (*)"],
				studentName: row["Họ và tên (*)"],
				dateOfBirth: row["Ngày sinh (*)"],
				placeOfBirth: row["Nơi sinh"],
				gender: row["Giới tính (*)"],
				ethnicCode: row["Mã Dân tộc"],
				ethnicity: row["Dân tộc"],
				nationalityCode: row["Mã quốc tịch"],
				nationality: row["Quốc tịch"],
				certificateNumber: row["Số hiệu bằng (cơ sở) (*)"],
				registrationNumber: row["Số vào sổ cấp bằng (*)"],
				signedBy: row["Người ký (*)"],
				signerTitle: row["Chức danh người ký (*)"],
				signDate: row["Ngày ký (*)"],
				course: row["Khóa học (*)"],
				language: row["Ngoại ngữ (*)"],
				level: row["Trình độ ngoại ngữ (*)"],
				courseStart: row["Khoá học bắt đầu"],
				courseEnd: row["Khoá học kết thúc"],
				examCenter: row["Hội đồng thi (*)"],
				decisionToEstablishCouncilNumber: row["Quyết định thành lập hội đồng (*)"],
				recognitionDecisionNumber: row["Quyết định công nhận số (*)"],
				requestTypeId: row["Loại yêu cầu ID (*)"],
				requestType: row["Loại yêu cầu (*)"],
				notes: row["Ghi chú"],
				status: row.status, // Server will set default status
			}));

			let validatedImportData = validateLanguageCertificateImportData(importedDataFromExcel);
			let hasErrors = validatedImportData.some((x) => x.errors.length !== 0);

			if (hasErrors) {
				return validatedImportData;
			}
			// validatedImportData.forEach((row) => {
			// 	if (row.errors.length > 0) {
			// 		console.log("Validation errors for row:", row, row.errors);
			// 	}
			// });

			validatedImportData = await validateLanguageCertificateImportDataInDB(validatedImportData);
			hasErrors = validatedImportData.some((x) => x.errors.length !== 0);

			if (hasErrors) {
				return validatedImportData;
			}

			//= Nếu không lỗi thì format lại dữ liệu rồi gửi dữ liệu import đến back-end
			const formattedImportData = validatedImportData.map((x) => {
				return {
					...x,
					dateOfBirth: format(parse(x.dateOfBirth, "dd/MM/yyyy", new Date()), "yyyy-MM-dd", { locale: vi }),
					courseStart: x.courseStart ? format(parse(x.courseStart, "dd/MM/yyyy", new Date()), "yyyy-MM-dd", { locale: vi }) : null,
					courseEnd: x.courseEnd ? format(parse(x.courseEnd, "dd/MM/yyyy", new Date()), "yyyy-MM-dd", { locale: vi }) : null,
				};
			});

			const result = await languageCertificatesApi.bulkImport(formattedImportData);

			fetchList({});
			return undefined;
		} catch (error) {
			console.error("Import error:", error);
		}
	};

	const handleExport = useCallback(async () => {
		if (!canExport) {
			notify.error("Bạn không có quyền export chứng chỉ");
			return [];
		}

		const exportData = await getFullRecord(isFiltering ? autoFilters : {});

		return exportData.map((row) => ({
			"CCCD (*)": row.cccd,
			"Số báo danh (*)": row.studentId,
			"Họ và tên (*)": row.studentName,
			"Ngày sinh (*)": row.dateOfBirth ? format(new Date(row.dateOfBirth), "dd/MM/yyyy") : "",
			"Nơi sinh": row.placeOfBirth,
			"Giới tính (*)": row.gender,
			"Mã Dân tộc": row.ethnicCode,
			"Dân tộc": row.ethnicity,
			"Mã quốc tịch": row.nationalityCode,
			"Quốc tịch": row.nationality,
			"Số hiệu bằng (cơ sở) (*)": row.certificateNumber,
			"Số vào sổ cấp bằng (*)": row.registrationNumber,
			"Người ký (*)": row.signedBy,
			"Chức danh người ký (*)": row.signerTitle,
			"Ngày ký (*)": row.signDate ? format(new Date(row.signDate), "dd/MM/yyyy") : "",
			"Khóa học (*)": row.course,
			"Ngoại ngữ (*)": row.language,
			"Trình độ ngoại ngữ (*)": row.level,
			"Khoá học bắt đầu": row.courseStart ? format(new Date(row.courseStart), "dd/MM/yyyy") : "",
			"Khoá học kết thúc": row.courseEnd ? format(new Date(row.courseEnd), "dd/MM/yyyy") : "",
			"Hội đồng thi (*)": row.examCenter,
			"Quyết định thành lập hội đồng (*)": row.decisionToEstablishCouncilNumber,
			"Quyết định công nhận số (*)": row.recognitionDecisionNumber,
			"Loại yêu cầu ID (*)": row.requestTypeId,
			"Loại yêu cầu (*)": row.requestType,
			"Ghi chú": row.notes,
		}));
	}, [autoFilters, isFiltering, canExport]);

	const handleExportErrors = (data: any[]) => {
		// Chuyển đổi dữ liệu có lỗi sang định dạng phù hợp để xuất file, không nên biến đổi giá trị
		return data.map((row) => {
			return {
				"CCCD (*)": row.cccd,
				"Số báo danh (*)": row.studentId,
				"Họ và tên (*)": row.studentName,
				"Ngày sinh (*)": row.dateOfBirth,
				"Nơi sinh": row.placeOfBirth,
				"Giới tính (*)": row.gender,
				"Mã Dân tộc": row.ethnicCode,
				"Dân tộc": row.ethnicity,
				"Mã quốc tịch": row.nationalityCode,
				"Quốc tịch": row.nationality,
				"Số hiệu bằng (cơ sở) (*)": row.certificateNumber,
				"Số vào sổ cấp bằng (*)": row.registrationNumber,
				"Người ký (*)": row.signedBy,
				"Chức danh người ký (*)": row.signerTitle,
				"Ngày ký (*)": row.signDate,
				"Khóa học (*)": row.course,
				"Ngoại ngữ (*)": row.language,
				"Trình độ ngoại ngữ (*)": row.level,
				"Khoá học bắt đầu": row.courseStart,
				"Khoá học kết thúc": row.courseEnd,
				"Hội đồng thi (*)": row.examCenter,
				"Quyết định thành lập hội đồng (*)": row.decisionToEstablishCouncilNumber,
				"Quyết định công nhận số (*)": row.recognitionDecisionNumber,
				"Loại yêu cầu ID (*)": row.requestTypeId,
				"Loại yêu cầu (*)": row.requestType,
				"Ghi chú": row.notes,
				Lỗi: row.errors,
			}
		}
		)
	}

	const handleAdd = async (newCertificate: Omit<iLanguageCertificate, "id">) => {
		if (!canCreate) {
			notify.error("Bạn không có quyền thêm chứng chỉ ngoại ngữ");
			return;
		}

		try {
			await languageCertificatesApi.create(newCertificate);
			fetchList({});
			notify.success("Thêm chứng chỉ ngoại ngữ thành công");
		} catch (error) {
			console.error("Error adding language certificate:", error);
		}
	};

	const handleEdit = (certificate: LanguageCertificate) => {
		if (!canUpdate) {
			notify.error("Bạn không có quyền chỉnh sửa chứng chỉ ngoại ngữ");
			return;
		}
		setEditingCertificate(certificate);
		setModalOpen(true);
	};

	const handleUpdate = async (updatedCertificate: LanguageCertificate) => {
		if (!canUpdate) {
			notify.error("Bạn không có quyền cập nhật chứng chỉ ngoại ngữ");
			return;
		}

		try {
			await languageCertificatesApi.update(updatedCertificate.id, updatedCertificate);
			fetchList({});
			notify.success("Cập nhật chứng chỉ ngoại ngữ thành công");
		} catch (error) {
			console.error("Error updating language certificate:", error);
		}
	};

	const handleDelete = async () => {
		if (!canDelete) {
			notify.error("Bạn không có quyền xóa chứng chỉ ngoại ngữ");
			return;
		}
		if (deleteItemId == null) {
			notify.error("Chưa chọn chứng chỉ cần xóa");
			return;
		}
		try {
			await languageCertificatesApi.delete(deleteItemId);
			notify.success("Đã xóa chứng chỉ ngoại ngữ thành công");

			// If we're on the last page and it becomes empty, go to previous page
			if (certificates.length === 1 && currentPage > 1) {
				fetchList();
			} else {
				fetchList({});
			}
		} catch (error) {
			console.error("Error deleting language certificate:", error);
		}
	};

	const startFilters = () => {
		setIsFiltering(true);
		fetchList();
	};


	const clearFilters = () => {
		setRequestTypeFilter("all");
		setLanguageFilter("all");
		setDateFromFilter("");
		setLanguageLevelFilter("");
		setDateToFilter("");
		setSearchTerm("");
		setCurrentPage(1);
		setLanguages([]);
		setLanguageLevels([]);
		fetchList({});
	};

	const handleFilterChange = (setter: Function, value: string) => {
		setter(value);
		setCurrentPage(1);
	};

	useEffect(() => {
		setAutoFilters({
			limit: itemsPerPage,
			search: searchTerm.trim().toLocaleLowerCase(),
			level: languageLevelFilter,
			requestType: requestTypeFilter,
			language: languageFilter !== "all" ? languageFilter : undefined,
			courseStart: dateFromFilter || "",
			courseEnd: dateToFilter || "",
		});
	}, [itemsPerPage, searchTerm, requestTypeFilter, languageFilter, dateFromFilter, dateToFilter, languageLevelFilter]);


	return (
		<div className='space-y-6'>
			<div className='flex items-center justify-between'>
				<div>
					<h2 className='text-2xl mb-2'>Quản lý Chứng chỉ Ngoại ngữ</h2>
					{/* <p className='text-muted-foreground'>Quản lý thông tin chứng chỉ ngoại ngữ</p> */}
				</div>
				<div className='flex gap-2'>
					<Button variant='outline' onClick={() => fetchList()} disabled={loading}>
						<RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
						Làm mới
					</Button>
					{canCreate && (
						<Button
							variant={"add"}
							onClick={() => {
								setModalOpen(true);
								setModalMode("create");
							}}
						>
							<Plus className='w-4 h-4' />
							Thêm chứng chỉ
						</Button>
					)}
					{(canImport || canExport) && (
						<ImportExportActions
							dataType="language"
							onImport={
								canImport
									? handleImport
									: async (data: any[], mode: ImportMode) => {
										return [];
									}
							}
							onExport={canExport ? handleExport : async () => []}
							onExportError={handleExportErrors}
							templateData={generateLanguageCertificateTemplate()}
							entityName='Chứng chỉ Ngoại ngữ'
							sampleFileName={`DSCCNN_${format(new Date(), "ddMMyyyy")}`}
							sampleErrorFileName={`DSCCNN_Errors_${format(new Date(), "ddMMyyyy")}`}
							validHeaderImport={[
								"CCCD (*)",
								"Số báo danh (*)",
								"Họ và tên (*)",
								"Ngày sinh (*)",
								"Nơi sinh",
								"Giới tính (*)",
								"Mã Dân tộc",
								"Dân tộc",
								"Mã quốc tịch",
								"Quốc tịch",
								"Số hiệu bằng (cơ sở) (*)",
								"Số vào sổ cấp bằng (*)",
								"Người ký (*)",
								"Chức danh người ký (*)",
								"Ngày ký (*)",
								"Khóa học (*)",
								"Ngoại ngữ (*)",
								"Trình độ ngoại ngữ (*)",
								"Khoá học bắt đầu",
								"Khoá học kết thúc",
								"Hội đồng thi (*)",
								"Quyết định thành lập hội đồng (*)",
								"Quyết định công nhận số (*)",
								"Loại yêu cầu ID (*)",
								"Loại yêu cầu (*)",
								"Ghi chú",
							]}
						/>
					)}
				</div>
			</div>

			<Card>
				<CardHeader>
					<CardTitle className='flex items-center gap-2'>
						{/* {loading && <Loader2 className="w-4 h-4 animate-spin" />} */}
						Danh Sách Chứng Chỉ
					</CardTitle>
					<div className='flex flex-wrap items-center gap-4 mt-4'>
						<div className='relative'>
							<Search className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4' />
							<NgocInput placeholder='Tìm kiếm theo tên, CCCD, Số báo danh' value={searchTerm} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleFilterChange(setSearchTerm, e.target.value)} maxLength={100} className="pl-8 min-w-96" />
						</div>
						<Select value={requestTypeFilter} onValueChange={(value) => handleFilterChange(setRequestTypeFilter, value)}>
							<SelectTrigger className='w-48'>
								<SelectValue placeholder='Loại yêu cầu' />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value='all'>Tất cả loại yêu cầu</SelectItem>
								{requestTypeList.map((tp) => {
									return (
										<SelectItem key={tp.code} value={tp.name}>
											{tp.name}
										</SelectItem>
									);
								})}
							</SelectContent>
						</Select>
						<>
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<Button variant='outline' className={cn("w-52 justify-between text-slate-700 font-normal")}>
										{languages.length === 1 ? languageOptions.find((x) => x.value === languages[0])?.label : languages.length > 1 ? `Đã chọn ${languages.length} ngoại ngữ` : "Ngoại ngữ"}
										<ChevronDown />
									</Button>
								</DropdownMenuTrigger>
								<DropdownMenuContent>
									{languageOptions.map((option) => (
										<DropdownMenuCheckboxItem
											key={option.value}
											checked={languages.includes(option.value)}
											onCheckedChange={() => toggleSelectLanguage(option.value)}
											className='capitalize cursor-pointer'
										>
											{option.label}
										</DropdownMenuCheckboxItem>
									))}
								</DropdownMenuContent>
							</DropdownMenu>

							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<Button variant='outline' className={cn("w-60 justify-between text-slate-700 font-normal")}>
										{languageLevels.length === 1
											? languageLevelOptions.find((x) => x.value === languageLevels[0])?.label
											: languageLevels.length > 1
												? `Đã chọn ${languageLevels.length} cấp độ ngoại ngữ`
												: "Cấp độ ngoại ngữ"}
										<ChevronDown />
									</Button>
								</DropdownMenuTrigger>
								<DropdownMenuContent>
									{languageLevelOptions.map((option) => (
										<DropdownMenuCheckboxItem
											key={option.value}
											checked={languageLevels.includes(option.value)}
											onCheckedChange={() => toggleSelectLanguageLevel(option.value)}
											className='capitalize cursor-pointer'
										>
											{option.label}
										</DropdownMenuCheckboxItem>
									))}
								</DropdownMenuContent>
							</DropdownMenu>
						</>

						{/* <div className='flex items-center gap-2'>
							<Input type='date' placeholder='Từ ngày' value={dateFromFilter} onChange={(e) => handleFilterChange(setDateFromFilter, e.target.value)} className='w-36' />
							<span className='text-muted-foreground'>-</span>
							<Input type='date' placeholder='Đến ngày' value={dateToFilter} onChange={(e) => handleFilterChange(setDateToFilter, e.target.value)} className='w-36' />
						</div> */}
						<div className='flex items-center gap-2'>
							<Popover open={openCourseStartSelected} onOpenChange={setOpenCourseStartSelected}>
								<PopoverTrigger asChild>
									<Button variant='outline' className='w-full justify-start text-left font-normal bg-transparent'>
										<CalendarIcon className='mr-2 h-4 w-4' />
										{dateFromFilter === "" ? "Chọn ngày khóa học bắt đầu" : format(dateFromFilter, "dd/MM/yyyy", { locale: vi })}
									</Button>
								</PopoverTrigger>
								<PopoverContent className='w-auto p-0'>
									<Calendar
										mode='single'
										selected={dateFromFilter as unknown as Date}
										defaultMonth={dateFromFilter === "" ? undefined : new Date(dateFromFilter)}
										onSelect={(value: any) => {
											const localDate = new Date(value);
											localDate.setMinutes(localDate.getMinutes() - localDate.getTimezoneOffset());
											setDateFromFilter(localDate.toISOString());
											setOpenCourseStartSelected(false);
										}}
										initialFocus
									/>
								</PopoverContent>
							</Popover>

							<Popover open={openCourseEndSelected} onOpenChange={setOpenCourseEndSelected}>
								<PopoverTrigger asChild>
									<Button variant='outline' className='w-full justify-start text-left font-normal bg-transparent'>
										<CalendarIcon className='mr-2 h-4 w-4' />
										{dateToFilter === "" ? "Chọn ngày khóa học kết thúc" : format(dateToFilter, "dd/MM/yyyy", { locale: vi })}
									</Button>
								</PopoverTrigger>
								<PopoverContent className='w-auto p-0'>
									<Calendar
										mode='single'
										selected={dateToFilter as unknown as Date}
										defaultMonth={dateToFilter === "" ? undefined : new Date(dateToFilter)}
										onSelect={(value: any) => {
											const localDate = new Date(value);
											localDate.setMinutes(localDate.getMinutes() - localDate.getTimezoneOffset());
											setDateToFilter(localDate.toISOString());
											setOpenCourseEndSelected(false);
										}}
										initialFocus
									/>
								</PopoverContent>
							</Popover>
						</div>

						<Button size={"sm"} variant='edit' onClick={() => startFilters()}>
							Lọc
						</Button>
						<Button size={"sm"} variant='outline' onClick={clearFilters}>
							Đặt lại
						</Button>
					</div>
				</CardHeader>
				<CardContent>
					{/* Table with horizontal scroll */}
					<Select value={itemsPerPage.toString()} onValueChange={(value) => {
						setItemsPerPage(Number(value));
						fetchList({
							...autoFilters,
							limit: Number(value),
							page: 1
						});
					}}>
						<SelectTrigger className='w-20'>
							<SelectValue placeholder='Hình thức đào tạo' />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value='10'>10</SelectItem>
							<SelectItem value='15'>15</SelectItem>
							<SelectItem value='20'>20</SelectItem>
							<SelectItem value='50'>50</SelectItem>
						</SelectContent>
					</Select>
					<div className='relative rounded-md border mt-2'>
						<SmoothLoadingWrapper loading={loading}>
							<Table className='min-w-[1200px]'>
								<TableHeader>
									<TableRow>
										<TableHead className='w-[150px]'>Họ tên</TableHead>
										<TableHead className='w-[100px]'>Ngoại ngữ</TableHead>
										<TableHead className='w-[150px]'>Số hiệu chứng chỉ</TableHead>
										<TableHead className='w-[150px]'>Trạng thái</TableHead>
										{/* <TableHead className='w-[120px]'>Số báo danh</TableHead> */}
										{/* <TableHead className='w-[130px]'>CCCD</TableHead> */}
										<TableHead className='w-[80px]'>Trình độ</TableHead>
										<TableHead className='w-[100px]'>Loại yêu cầu</TableHead>
										{/* <TableHead className='w-[100px]'>Ngày cấp</TableHead> */}
										<TableHead className='w-[100px]'>Trung tâm thi</TableHead>
										{/* <TableHead className='w-[120px]'>Ngày hoàn thành</TableHead> */}
										{/* <TableHead className='w-[140px]'>Loại yêu cầu</TableHead> */}
										<TableHead className='w-[120px]'>Thao tác</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{
										certificates.length === 0 ? (
											<TableRow>
												<TableCell colSpan={8} className='text-center py-8 text-muted-foreground'>
													{searchTerm || languageFilter ? "Không tìm thấy kết quả phù hợp" : "Không có dữ liệu"}
												</TableCell>
											</TableRow>
										) : (
											certificates.map((cert) => (
												<TableRow key={cert.id}>
													<TableCell className='whitespace-normal text-sm' style={{ wordBreak: 'break-word', textTransform: "capitalize" }}>
														{cert.studentName}
													</TableCell>
													<TableCell className='whitespace-normal text-sm' style={{ wordBreak: 'break-word', textTransform: "capitalize" }}>{cert.language}</TableCell>
													<TableCell className='whitespace-normal text-sm' style={{ wordBreak: 'break-word', textTransform: "capitalize" }}>{cert.certificateNumber}</TableCell>
													<TableCell className='whitespace-normal text-sm' style={{ wordBreak: 'break-word', textTransform: "capitalize" }}>
														<StatusBadge name={cert.status} />
													</TableCell>
													{/* <TableCell className='font-mono text-sm whitespace-normal'>{cert.studentId}</TableCell> */}
													{/* <TableCell className='font-mono text-sm whitespace-normal'>{cert.cccd}</TableCell> */}
													<TableCell className='whitespace-normal'>
														<Badge variant='outline' className='text-xs'>
															{cert.level}
														</Badge>
													</TableCell>
													<TableCell className='whitespace-normal'>
														<Badge variant='outline' className='text-xs'>
															{cert.requestType}
														</Badge>
													</TableCell>
													{/* <TableCell className='whitespace-normal'>{format(cert.issueDate, "dd/MM/yyyy")}</TableCell> */}
													<TableCell className='whitespace-normal'>{cert.examCenter}</TableCell>
													{/* <TableCell className='whitespace-normal'>{new Date(cert.courseEnd).toLocaleDateString("vi-VN")}</TableCell> */}
													{/* <TableCell className='whitespace-normal'>
														<Badge variant='outline' className='text-xs'>
															{cert.requestType}
														</Badge>
													</TableCell> */}
													<TableCell className='whitespace-normal'>
														<div className='flex items-center gap-1'>
															<Button
																onClick={() => {
																	setCertificateInformation(cert);
																}}
																variant='ghost'
																className='h-8 w-8 p-0'
															>
																<Eye className='w-4 h-4' />
															</Button>
															{canUpdate && (
																<Button
																	variant='ghost'
																	onClick={() => {
																		handleEdit(cert);
																		setModalMode("edit");
																	}}
																	className='h-8 w-8 p-0 text-yellow-600 hover:text-yellow-600'
																>
																	<Edit className='w-4 h-4' />
																</Button>
															)}
															{canDelete && (
																<Button variant='ghost' onClick={() => setDeleteItemId(cert.id)} className='h-8 w-8 p-0 text-destructive hover:text-destructive'>
																	<Trash2 className='w-4 h-4' />
																</Button>
															)}
														</div>
													</TableCell>
												</TableRow>
											))
										)}
								</TableBody>
							</Table>
						</SmoothLoadingWrapper>
					</div>

					{/* Pagination */}
					{!loading && totalPages > 1 && <PaginationIndex setCurrentPage={setCurrentPage} currentPage={currentPage} itemsPerPage={itemsPerPage} totalCount={totalCount} totalPages={totalPages} />}
				</CardContent>
			</Card>

			<LanguageCertificateModal isOpen={isModalOpen} onClose={() => setModalOpen(false)} onAdd={handleAdd} editData={editingCertificate} onUpdate={handleUpdate} mode={modalMode} />

			<LanguageCertificateDetail data={certificateInformation} setDegreeInformation={setCertificateInformation} />

			<AlertDialog open={deleteItemId !== null} onOpenChange={() => setDeleteItemId(null)}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Xác nhận xóa</AlertDialogTitle>
						<AlertDialogDescription>Bạn có chắc chắn muốn xóa? Hành động này không thể hoàn tác.</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Hủy</AlertDialogCancel>
						<AlertDialogAction onClick={handleDelete} className='bg-destructive text-destructive-foreground hover:bg-destructive/90'>
							Xóa
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}
